/**
 * Route Configuration
 * Centralized route definitions for the subscription platform
 */

import React from 'react';
import { lazy } from 'react';
import type { UserRole } from '@/common/stores';

/**
 * Lazy loaded pages (for code splitting)
 */
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const SettingsPage = lazy(() =>
  import('@/pages/common/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const AdminDashboard = lazy(() =>
  import('@/pages/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard }))
);
const AdminUsersPage = lazy(() =>
  import('@/pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage }))
);
const AdminPlatformSubscriptionsPage = lazy(() =>
  import('@/pages/admin/AdminPlatformSubscriptionsPage').then((m) => ({ default: m.AdminPlatformSubscriptionsPage }))
);
const AdminCreatePlatformSubscriptionPage = lazy(() =>
  import('@/pages/admin/AdminCreatePlatformSubscriptionPage').then((m) => ({ default: m.AdminCreatePlatformSubscriptionPage }))
);
const AdminDisputesPage = lazy(() =>
  import('@/pages/admin/AdminDisputesPage').then((m) => ({ default: m.AdminDisputesPage }))
);
const AdminReportsPage = lazy(() =>
  import('@/pages/admin/AdminReportsPage').then((m) => ({ default: m.AdminReportsPage }))
);
const AdminPaymentsPage = lazy(() =>
  import('@/pages/admin/AdminPaymentsPage').then((m) => ({ default: m.AdminPaymentsPage }))
);
const AdminSubAccountsPage = lazy(() =>
  import('@/pages/admin/AdminSubAccountsPage').then((m) => ({ default: m.AdminSubAccountsPage }))
);
const AdminPayoutsPage = lazy(() =>
  import('@/pages/admin/AdminPayoutsPage').then((m) => ({ default: m.AdminPayoutsPage }))
);
const AdminGroupsPage = lazy(() =>
  import('@/pages/admin/AdminGroupsPage').then((m) => ({ default: m.AdminGroupsPage }))
);
const AdminSettlementsPage = lazy(() =>
  import('@/pages/admin/AdminSettlementsPage').then((m) => ({ default: m.AdminSettlementsPage }))
);
const AdminBroadcastNotificationPage = lazy(() =>
  import('@/pages/admin/AdminBroadcastNotificationPage').then((m) => ({
    default: m.AdminBroadcastNotificationPage,
  }))
);
const AdminBroadcastHistoryPage = lazy(() =>
  import('@/pages/admin/AdminBroadcastHistoryPage').then((m) => ({
    default: m.AdminBroadcastHistoryPage,
  }))
);
const AdminEmailTesterPage = lazy(() =>
  import('@/pages/admin/AdminEmailTesterPage').then((m) => ({
    default: m.AdminEmailTesterPage,
  }))
);
const NotFoundPage = lazy(() =>
  import('@/pages/common/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
const UnauthorizedPage = lazy(() =>
  import('@/pages/common/UnauthorizedPage').then((m) => ({ default: m.UnauthorizedPage }))
);

/**
 * Route configuration object
 */
export interface RouteConfig {
  path: string;
  element: React.ReactNode;
  requiresAuth?: boolean;
  requiredRoles?: UserRole | UserRole[];
  skipLayout?: boolean;
}

/**
 * Public Routes (no authentication required)
 */
export const PUBLIC_ROUTES: RouteConfig[] = [
  {
    path: '/login',
    element: <LoginPage />,
    skipLayout: true,
  },
];

/**
 * Authenticated Routes (authentication required)
 */
export const AUTH_ROUTES: RouteConfig[] = [
  {
    path: '/',
    element: <DashboardPage />,
  },
  {
    path: '/dashboard',
    element: <DashboardPage />,
    requiresAuth: true,
  },
  {
    path: '/settings',
    element: <SettingsPage />,
    requiresAuth: true,
  },
];

/**
 * Admin Routes (authentication + admin role required)
 */
export const ADMIN_ROUTES: RouteConfig[] = [
  {
    path: '/admin',
    element: <AdminDashboard />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/users',
    element: <AdminUsersPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/platform-subscriptions',
    element: <AdminPlatformSubscriptionsPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/platform-subscriptions/create',
    element: <AdminCreatePlatformSubscriptionPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/platform-subscriptions/:planId/edit',
    element: <AdminCreatePlatformSubscriptionPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/disputes',
    element: <AdminDisputesPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/reports',
    element: <AdminReportsPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/payments',
    element: <AdminPaymentsPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/subaccounts',
    element: <AdminSubAccountsPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/payouts',
    element: <AdminPayoutsPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/groups',
    element: <AdminGroupsPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/settlements',
    element: <AdminSettlementsPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/notifications',
    element: <AdminBroadcastHistoryPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/notifications/new',
    element: <AdminBroadcastNotificationPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    // Backwards-compatible alias — the Broadcast History is now the default
    // Notifications view at `/admin/notifications`.
    path: '/admin/notifications/history',
    element: <AdminBroadcastHistoryPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
  {
    path: '/admin/email-tester',
    element: <AdminEmailTesterPage />,
    requiresAuth: true,
    requiredRoles: 'ADMIN',
  },
];

/**
 * Error Routes
 */
export const ERROR_ROUTES: RouteConfig[] = [
  {
    path: '/unauthorized',
    element: <UnauthorizedPage />,
    skipLayout: true,
  },
  {
    path: '*',
    element: <NotFoundPage />,
    skipLayout: true,
  },
];

/**
 * All routes
 */
export const ALL_ROUTES: RouteConfig[] = [
  ...PUBLIC_ROUTES,
  ...AUTH_ROUTES,
  ...ADMIN_ROUTES,
  ...ERROR_ROUTES,
];
